import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';

const { 
  FiArrowLeft, FiSettings, FiEye, FiDownload, 
  FiCheck, FiX, FiFileText, FiBarChart2, 
  FiMessageSquare, FiLayout, FiType, FiGlobe
} = FiIcons;

const CustomReportBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const report = reportsData[id];

  const [config, setConfig] = useState({
    title: report?.assessmentTitle || 'Custom Business Report',
    showSummary: true,
    showBenchmarks: true,
    showRecommendations: true,
    showDetailedScores: true,
    includeExpertNotes: false,
    format: 'pdf',
    theme: 'modern'
  });

  const [notes, setNotes] = useState('');

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Report not found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const toggleConfig = (key) => {
    setConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <Link to={`/report/${id}`} className="text-gray-600 hover:text-blue-600 flex items-center mb-4 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Report View
          </Link>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Custom Report Builder</h1>
              <p className="text-gray-600">Configure and export a tailored version of your results.</p>
            </div>
            <div className="flex gap-3">
              <button className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 shadow-sm flex items-center">
                <SafeIcon icon={FiEye} className="mr-2" /> Preview
              </button>
              <button className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-md flex items-center">
                <SafeIcon icon={FiDownload} className="mr-2" /> Generate {config.format.toUpperCase()}
              </button>
            </div>
          </div>
        </header>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2" /> Report Configuration
              </h3>
              
              <div className="space-y-6">
                {/* Report Title */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Report Title</label>
                  <div className="relative">
                    <SafeIcon icon={FiType} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="text" 
                      value={config.title}
                      onChange={(e) => setConfig({...config, title: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                {/* Section Toggles */}
                <div className="space-y-3">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Include Sections</label>
                  {[
                    { key: 'showSummary', label: 'Executive Summary', icon: FiFileText },
                    { key: 'showDetailedScores', label: 'Detailed Scoring', icon: FiBarChart2 },
                    { key: 'showRecommendations', label: 'Recommendations', icon: FiCheck },
                    { key: 'showBenchmarks', label: 'Industry Benchmarking', icon: FiGlobe },
                    { key: 'includeExpertNotes', label: 'Expert Annotations', icon: FiMessageSquare }
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => toggleConfig(item.key)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        config[item.key] 
                          ? 'bg-blue-50 border-blue-200 text-blue-700' 
                          : 'bg-white border-gray-100 text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={item.icon} className={config[item.key] ? 'text-blue-600' : 'text-gray-400'} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${config[item.key] ? 'bg-blue-600 text-white' : 'bg-gray-100 text-transparent'}`}>
                        <SafeIcon icon={FiCheck} className="w-3 h-3" />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Export Format */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Export Format</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['pdf', 'csv', 'ppt'].map(f => (
                      <button
                        key={f}
                        onClick={() => setConfig({...config, format: f})}
                        className={`py-2 rounded-lg text-xs font-bold transition-all border ${
                          config.format === f 
                            ? 'bg-slate-900 text-white border-slate-900' 
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center">
                <SafeIcon icon={FiMessageSquare} className="mr-2" /> Custom Annotations
              </h3>
              <textarea 
                placeholder="Add notes for your team or stakeholders..."
                className="w-full bg-gray-50 border-none rounded-xl p-4 text-sm focus:ring-2 focus:ring-blue-500 h-32 resize-none"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              ></textarea>
            </section>
          </div>

          {/* Real-time Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden min-h-[800px] flex flex-col">
              <div className="bg-slate-50 border-b border-gray-100 p-4 flex justify-between items-center">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Live Preview</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-400"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                </div>
              </div>
              
              <div className="p-12 flex-1 font-serif">
                {/* Preview Header */}
                <div className="border-b-4 border-slate-900 pb-8 mb-12">
                  <div className="flex justify-between items-end">
                    <div>
                      <h2 className="text-3xl font-bold text-slate-900 mb-2">{config.title}</h2>
                      <p className="text-slate-500 italic">Prepared for Internal Stakeholders • {new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-black text-blue-600">{report.overallScore}%</div>
                      <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Performance Index</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-10">
                  {config.showSummary && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <h4 className="text-lg font-bold text-slate-900 uppercase tracking-wide mb-4 flex items-center">
                        <span className="w-8 h-[2px] bg-blue-600 mr-3"></span> Executive Summary
                      </h4>
                      <p className="text-slate-700 leading-relaxed italic border-l-4 border-slate-100 pl-6">
                        "{report.summary}"
                      </p>
                    </motion.div>
                  )}

                  {config.showDetailedScores && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <h4 className="text-lg font-bold text-slate-900 uppercase tracking-wide mb-6 flex items-center">
                        <span className="w-8 h-[2px] bg-blue-600 mr-3"></span> Departmental Performance
                      </h4>
                      <div className="grid grid-cols-2 gap-8">
                        {report.sections.map((section, idx) => (
                          <div key={idx} className="bg-slate-50 p-6 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                              <span className="font-bold text-slate-800">{section.title}</span>
                              <span className="text-blue-600 font-black">{section.score}%</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1 rounded-full">
                              <div className="bg-slate-900 h-full" style={{ width: `${section.score}%` }}></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {config.showRecommendations && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <h4 className="text-lg font-bold text-slate-900 uppercase tracking-wide mb-4 flex items-center">
                        <span className="w-8 h-[2px] bg-blue-600 mr-3"></span> Action Roadmap
                      </h4>
                      <div className="space-y-4">
                        {report.sections.flatMap(s => s.recommendations).slice(0, 4).map((rec, idx) => (
                          <div key={idx} className="flex items-start gap-4">
                            <span className="text-slate-300 font-black text-2xl leading-none">0{idx + 1}</span>
                            <p className="text-slate-700 text-sm pt-1">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {notes && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-yellow-50 p-6 rounded-xl border border-yellow-100">
                      <h4 className="text-xs font-bold text-yellow-800 uppercase tracking-widest mb-2 flex items-center">
                        <SafeIcon icon={FiMessageSquare} className="mr-2" /> Strategic Notes
                      </h4>
                      <p className="text-slate-800 text-sm whitespace-pre-wrap">{notes}</p>
                    </motion.div>
                  )}
                </div>

                <div className="mt-20 pt-8 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Cansulta Business 360 Confidential</span>
                  <span className="text-[10px] text-slate-400 font-bold">Page 01 of 12</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;